import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Navbar.css';
import { personalInfo } from '../config';
import { getLenis } from '../hooks/useLenis';

const SunIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
);

const MoonIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
);

const Navbar = ({ darkMode, toggleDarkMode }) => {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState('hero');
  const lastY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 40);
      setHidden(y > lastY.current && y > 100);
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll('section[id]');
    const obs = new IntersectionObserver(e => e.forEach(en => { if (en.isIntersecting) setActive(en.target.id); }), { threshold: 0.4 });
    sections.forEach(s => obs.observe(s));
    return () => sections.forEach(s => obs.unobserve(s));
  }, []);

  const navItems = [
    { name: 'About', href: '#about' },
    { name: 'Projects', href: '#projects' },
    { name: 'Experience', href: '#experience' },
    { name: 'Contact', href: '#contact' },
  ];

  const go = useCallback((e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const l = getLenis();
    if (l) l.scrollTo(href);
    else document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <motion.nav className={`navbar ${scrolled ? 'scrolled' : ''}`} role="navigation" animate={{ y: hidden ? -80 : 0, opacity: hidden ? 0 : 1 }} transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}>
      <div className="nav-inner">
        <a href="#hero" className="nav-logo" onClick={e => go(e, '#hero')}>MV</a>

        <div className={`nav-links ${mobileOpen ? 'open' : ''}`}>
          {navItems.map(item => (
            <a key={item.name} href={item.href} className={`nav-link ${active === item.href.slice(1) ? 'active' : ''}`} onClick={e => go(e, item.href)}>{item.name}</a>
          ))}
        </div>

        <div className="nav-right">
          <a href={personalInfo.resumeLink} target="_blank" rel="noopener noreferrer" className="nav-resume">Resume</a>
          <button className="nav-theme" onClick={toggleDarkMode} aria-label="Toggle theme">
            <AnimatePresence mode="wait">
              {darkMode ? (
                <motion.span key="s" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <SunIcon />
                </motion.span>
              ) : (
                <motion.span key="m" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <MoonIcon />
                </motion.span>
              )}
            </AnimatePresence>
          </button>
          <button className={`nav-menu-btn ${mobileOpen ? 'open' : ''}`} onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu"><span /><span /></button>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar;
